// Store-level invalidation also covers updates while ChatList is unmounted.
const CHANGES = new Set([
  'ADD_MESSAGE', 'DELETE_MESSAGE', 'ADD_CONVERSATION', 'UPDATE_CONVERSATION',
  'DELETE_CONVERSATION', 'ASSIGN_AGENT', 'UPDATE_ASSIGNEE', 'ASSIGN_TEAM',
  'ASSIGN_PRIORITY', 'CHANGE_CONVERSATION_STATUS', 'UPDATE_CONVERSATION_LAST_ACTIVITY',
  'UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES', 'UPDATE_CONVERSATION_CONTACT',
  'UPDATE_MESSAGE_UNREAD_COUNT', 'APPLY_PERSONAL_READ',
  'conversationLabels/SET_CONVERSATION_LABELS',
]);

export function installConversationQueryLifecycle(store) {
  let timer;
  let refreshing = false;
  let refreshGeneration = 0;
  let nextRefreshAt = 0;
  const schedule = () => {
    if (timer || refreshing) return;
    timer = setTimeout(() => {
      timer = null;
      const cache = store.state.conversationQueryCache;
      const entry = cache.queries[cache.activeKey];
      if (!entry?.dirty || entry.error || store.state.conversations.listLoadingStatus ||
          (typeof document !== 'undefined' && document.visibilityState !== 'visible')) return;
      const pending = store.dispatch('fetchAllConversations', { refresh: true });
      // BEGIN_LIST_REQUEST runs synchronously; own its resulting generation.
      const generation = refreshGeneration;
      refreshing = true;
      Promise.resolve(pending).finally(() => {
        if (generation !== refreshGeneration) return;
        refreshing = false;
        // Give readers a pagination window even when every refresh overlaps
        // another live event. Dirty state remains pending, not discarded.
        nextRefreshAt = Date.now() + 5000;
        const active = store.state.conversationQueryCache;
        if (active.queries[active.activeKey]?.dirty) schedule();
      });
    }, Math.max(150, nextRefreshAt - Date.now()));
  };
  store.watch((_, getters) => {
    const account = getters.getCurrentAccount || {};
    return JSON.stringify([getters.getCurrentAccountId, getters.getCurrentUserID,
      account.role, account.custom_role_id, [...(account.permissions || [])].sort()]);
  }, () => {
    const active = store.state.conversationQueryCache.activeKey;
    store.dispatch('clearListContext');
    if (active && store.getters.getCurrentAccountId && store.getters.getCurrentUserID) {
      const epoch = store.state.conversationQueryCache.identityEpoch;
      Promise.resolve().then(() => {
        if (epoch === store.state.conversationQueryCache.identityEpoch && store.getters.getCurrentUserID) {
          store.dispatch('fetchAllConversations', { activate: true });
        }
      });
    }
  }, { flush: 'sync' });
  store.subscribe(({ type }) => {
    if (['BEGIN_LIST_REQUEST', 'INVALIDATE_LIST_REQUEST', 'EMPTY_ALL_CONVERSATION', 'conversationQueryCache/clear'].includes(type)) {
      // Navigation, identity and reconnect must not wait for obsolete HTTP.
      refreshGeneration++;
      refreshing = false;
      if (type !== 'BEGIN_LIST_REQUEST') nextRefreshAt = 0;
      clearTimeout(timer);
      timer = null;
    }
    if (CHANGES.has(type)) store.commit('conversationQueryCache/dirty');
    if (CHANGES.has(type) || type === 'conversationQueryCache/dirty' || type === 'CLEAR_LIST_LOADING_STATUS') schedule();
  });
  // A metadata broadcast is not itself a permission revocation. Clear only
  // after the authorized inbox response actually removes a visible inbox.
  store.watch((_, getters) => (getters['inboxes/getInboxes'] || []).map(inbox => String(inbox.id)).sort(),
    (ids, previous = []) => {
      if (previous.some(id => !ids.includes(id))) store.dispatch('clearListContext');
    }, { flush: 'sync' });
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') store.dispatch('invalidateListQueries');
  });
}
